import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Bug, LoaderCircle, Paperclip, X, AlertTriangle } from 'lucide-react';
import { Modal } from './ui/Modal';
import CompactSelectPicker from './ui/CompactSelectPicker';
import { isTauri } from '../utils/tauri';
import reportService, { MAX_ATTACHMENTS } from '../services/reportService';

/**
 * "Report an issue", from Settings → About.
 *
 * Replaces a `mailto:` link, which on the desktop opened whatever mail client
 * the OS happened to have configured — or nothing at all — and produced a
 * message with no version, no OS, and no way for us to reply in-app.
 *
 * The topic list comes from `/reports/meta` rather than being hardcoded, so the
 * dropdown cannot drift from the server's `validate_inclusion`. If that fetch
 * fails the modal still works: topics fall back to a local list and the server
 * remains the authority, so the worst case is a rejected submit with a clear
 * message rather than a modal that refuses to open.
 */

// Only used when /reports/meta is unreachable. Kept in sync with the server's
// @topics by the meta fetch, not by hand.
const FALLBACK_TOPICS = ['bug', 'feature', 'performance', 'billing', 'account', 'security', 'other'];

const labelStyle = {
  display: 'block',
  marginBottom: '0.375rem',
  fontSize: '0.6875rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-secondary, #6b7280)',
};

export default function ReportIssueModal({ isOpen, onClose, showToast }) {
  const { t } = useTranslation();
  const desktop = isTauri();

  const [topics, setTopics] = useState(FALLBACK_TOPICS);
  const [topic, setTopic] = useState('bug');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]); // server metadata
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    setTopic('bug');
    setSubject('');
    setBody('');
    setAttachments([]);
    setError('');
    setSending(false);
    setUploading(false);

    let cancelled = false;
    reportService
      .fetchMeta()
      .then((meta) => {
        if (cancelled) return;
        if (Array.isArray(meta?.topics) && meta.topics.length) setTopics(meta.topics);
      })
      .catch(() => {
        // Falls back to the local list — see the note above.
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Read by `addFiles` as it loops: `attachments` inside that closure is the
  // render-time value, so judging each file against it would let all five
  // through at once when several are picked in one go.
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  const addFiles = useCallback(
    async (files) => {
      const list = Array.from(files || []);
      if (!list.length) return;
      setError('');
      setUploading(true);
      try {
        for (const file of list) {
          // Checked per file inside the loop, not once up front: each accepted
          // upload changes the count the next file is judged against.
          const rejection = reportService.attachmentRejection(file, attachmentsRef.current.length);
          if (rejection === 'too_many') {
            setError(t('report.tooManyFiles', { count: MAX_ATTACHMENTS }));
            break;
          }
          if (rejection === 'too_large') {
            setError(t('report.fileTooLarge', { name: file.name }));
            continue;
          }
          const meta = await reportService.uploadAttachment(file);
          setAttachments((prev) => [...prev, meta]);
        }
      } catch (e) {
        setError(e?.response?.data?.error || e?.message || t('report.uploadFailed'));
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [t]
  );

  const removeAttachment = (url) => setAttachments((prev) => prev.filter((a) => a.url !== url));

  const canSubmit = topic && body.trim() && !sending && !uploading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSending(true);
    setError('');
    try {
      const diagnostics = await reportService.collectDiagnostics({ desktop });
      await reportService.submitReport({
        topic,
        subject,
        // A plain-text textarea, wrapped so the server (and the admin panel)
        // receive the HTML they expect. Newlines become breaks; the sanitiser
        // on both sides drops anything the user pasted that could execute.
        bodyHtml: `<p>${escapeHtml(body.trim()).replace(/\n/g, '<br>')}</p>`,
        diagnostics,
        attachments,
      });
      showToast?.(t('report.sent'), 'success');
      onClose();
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 429
          ? t('report.rateLimited')
          : err?.response?.data?.error ||
              firstFieldError(err?.response?.data?.errors) ||
              err?.message ||
              t('report.failed')
      );
    } finally {
      setSending(false);
    }
  };

  // SettingsModal wraps everything in an overlay carrying onClick={onClose},
  // with its dialog calling stopPropagation. Mounted inside that overlay, every
  // click in here — including into a text field — closed Settings and took this
  // modal with it mid-typing.
  //
  // THE stopPropagation IS THE FIX. React portals propagate events through the
  // REACT tree, not the DOM tree, so moving the markup to document.body changed
  // nothing about where the click went: this is a React child of SettingsModal
  // wherever its nodes land. Ablation says so plainly — portal alone still
  // fails 3 of the 4 isolation tests; stopPropagation alone passes all 4.
  //
  // The portal is kept for a DIFFERENT reason, not as a second line of defence
  // against this bug: rendering a fixed-position modal inside another dialog's
  // subtree makes it a hostage to that subtree's overflow and transforms, either
  // of which can clip it or trap it in the wrong stacking context. That risk is
  // real but silent, so it is worth keeping — as long as nobody reads it as
  // what stops the click.
  //
  // `display: contents` so the wrapper contributes no box and the modal's own
  // layout is untouched.
  return ReactDOM.createPortal(
    <div style={{ display: 'contents' }} onClick={(e) => e.stopPropagation()}>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('report.title')}
      subtitle={t('report.subtitle')}
      customIcon={<Bug size={24} className="text-blue-500" />}
      size="lg"
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {error && (
          <div className="report-error" role="alert">
            <AlertTriangle size={13} />
            <span>{error}</span>
          </div>
        )}

        <div>
          <label style={labelStyle} htmlFor="report-topic">
            {t('report.topic')}
          </label>
          {/* `flex w-full` because .modal-picker is inline-flex by default, so it
              shrinks to its label — a topic dropdown that changes width as you
              change topic, and does not line up with the fields beneath it.
              Same idiom as PublishToNetworkModal and ForkWorkspaceModal. */}
          <div className="modal-picker flex w-full">
          <CompactSelectPicker
            id="report-topic"
            value={topic}
            onChange={setTopic}
            options={topics.map((value) => ({
              value,
              label: t(`report.topics.${value}`, value),
            }))}
          />
          </div>
        </div>

        <div>
          <label style={labelStyle} htmlFor="report-subject">
            {t('report.subject')}
          </label>
          <input
            id="report-subject"
            type="text"
            className="modal-input"
            value={subject}
            maxLength={200}
            placeholder={t('report.subjectPlaceholder')}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle} htmlFor="report-body">
            {t('report.body')}
          </label>
          <textarea
            id="report-body"
            className="modal-input report-textarea"
            value={body}
            rows={8}
            placeholder={t('report.bodyPlaceholder')}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>{t('report.attachments')}</label>
          <div className="report-attach-row">
            <button
              type="button"
              className="settings-action-btn"
              disabled={uploading || attachments.length >= MAX_ATTACHMENTS}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <LoaderCircle size={13} className="animate-spin" />
              ) : (
                <Paperclip size={13} />
              )}
              {t('report.addFiles')}
            </button>
            <span className="report-attach-hint">
              {t('report.attachHint', { count: MAX_ATTACHMENTS })}
            </span>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => addFiles(e.target.files)}
          />
          {attachments.length > 0 && (
            <ul className="report-attach-list">
              {attachments.map((a) => (
                <li key={a.url}>
                  <span className="report-attach-name">{a.name}</span>
                  <button
                    type="button"
                    className="report-attach-remove"
                    onClick={() => removeAttachment(a.url)}
                    aria-label={t('report.removeFile', { name: a.name })}
                  >
                    <X size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Said plainly rather than buried: the report carries version and OS,
            and the user should know that before pressing send. */}
        <p className="report-privacy">{t('report.privacyNote')}</p>

        <div className="report-actions">
          <button type="button" className="settings-action-btn" onClick={onClose} disabled={sending}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="settings-action-btn settings-action-btn--primary" disabled={!canSubmit}>
            {sending && <LoaderCircle size={13} className="animate-spin" />}
            {t('report.send')}
          </button>
        </div>
      </form>
    </Modal>
    </div>,
    document.body
  );
}

/** Escapes before the body is wrapped in HTML, so typed markup stays literal. */
export function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Ecto returns `{field: ["message"]}`; show the first one rather than "failed". */
export function firstFieldError(errors) {
  if (!errors || typeof errors !== 'object') return null;
  for (const value of Object.values(errors)) {
    if (Array.isArray(value) && value.length) return String(value[0]);
    if (typeof value === 'string') return value;
  }
  return null;
}
